import { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

const ValidationForm = ({ children, fields: initialFields, validators, overrideFields = {}, validateInitialFields }) => {
  const runAllValidators = useCallback(
    (values) => {
      const invalidInputs = {};
      if (validators) {
        Object.keys(validators).forEach((fieldName) => {
          const validator = validators[fieldName];
          invalidInputs[fieldName] = !validator(values);
        });
      }

      return invalidInputs;
    },
    [validators]
  );

  const [datePickerErrorMessage, setDatePickerErrorMessage] = useState('');
  const [invalidInputs, setInvalidInputs] = useState(() => (validateInitialFields ? runAllValidators(initialFields) : {}));
  const [touchedInputs, setTouchedInputs] = useState({});
  const [fields, setFields] = useState(initialFields);

  const isMountedRef = useRef(false);
  const fieldsRef = useRef(fields);

  fieldsRef.current = fields;

  const onChange = useCallback(
    (e) => {
      if (e && e.target) {
        const { name, value, pattern, type, checked } = e.target;

        setFields((currentFields) => {
          const nextFields = {
            ...currentFields,
            [name]: type === 'checkbox' ? checked : value
          };

          let isValid = true;
          if (pattern) {
            const regex = new RegExp(pattern);
            isValid = regex.test(value);
          }

          setTouchedInputs((currentTouchedInputs) => ({
            ...currentTouchedInputs,
            [name]: true
          }));

          setInvalidInputs((currentInvalidInputs) => ({
            ...currentInvalidInputs,
            [name]: !isValid,
            ...runAllValidators(nextFields)
          }));

          return nextFields;
        });
      }
    },
    [runAllValidators]
  );

  const changeField = useCallback(
    (field) => (value) => {
      onChange({
        target: {
          name: field,
          value
        }
      });
    },
    [onChange]
  );

  const resetField = useCallback(
    (name) => {
      onChange({
        target: {
          name,
          value: initialFields[name]
        }
      });
    },
    [initialFields, onChange]
  );

  useEffect(() => {
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      return;
    }

    Object.keys(overrideFields).forEach((field) => {
      if (fieldsRef.current[field] !== undefined) {
        changeField(field)(overrideFields[field]);
      }
    });
  }, [overrideFields, changeField]);

  const hasInvalidInputs = Object.values(invalidInputs).some(Boolean);

  return children({
    invalidInputs,
    fields,
    onChange,
    changeField,
    datePickerErrorMessage,
    setDatePickerErrorMessage,
    resetField,
    hasInvalidInputs,
    touchedInputs
  });
};

ValidationForm.propTypes = {
  children: PropTypes.func.isRequired,
  fields: PropTypes.shape(),
  validators: PropTypes.objectOf(PropTypes.func.isRequired),
  overrideFields: PropTypes.shape(),
  validateInitialFields: PropTypes.bool
};

export default ValidationForm;
